import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocale } from 'next-intl';
import {
  fetchGalleryImages,
  fetchVideos,
  type GalleryImageItem,
  type VideoItem,
} from '@/app/_api/fetchGalleryContent';

type TaxonomyItem = {
  id: string;
  label: string;
  hide: boolean | null;
  createdAt: string;
  updatedAt: string;
};

type TaxonomyResponse = {
  docs: TaxonomyItem[];
  totalDocs: number;
  limit: number;
  totalPages: number;
  page: number;
  pagingCounter: number;
  hasPrevPage: boolean;
  hasNextPage: boolean;
  prevPage: number | null;
  nextPage: number | null;
};

type GalleryPageContextData = {
  // Taxonomy data
  useCaseTaxonomy: TaxonomyItem[];
  productsTaxonomy: TaxonomyItem[];

  // Filter states
  selectedUseCase: string;
  selectedProduct: string;
  searchQuery: string;

  // Gallery content
  galleryContent: (GalleryImageItem | VideoItem)[];

  // Loading states
  loading: boolean;
  contentLoading: boolean;
  error: string | null;

  // Block configuration
  useCaseTaxonomies?: any[] | null;
  productTaxonomies?: any[] | null;
  hideFilterBar?: boolean | null;
  hideSearchBar?: boolean | null;
};

type GalleryPageContextActions = {
  // Filter setters
  setSelectedUseCase: (value: string) => void;
  setSelectedProduct: (value: string) => void;
  setSearchQuery: (value: string) => void;

  // Manual refresh
  refreshContent: () => void;
};

type GalleryPageContextValue = {
  data: GalleryPageContextData;
  actions: GalleryPageContextActions;
};

const GalleryPageContext = createContext<GalleryPageContextValue | null>(null);

export const useGalleryPageState = (): GalleryPageContextValue => {
  const context = useContext(GalleryPageContext);
  if (!context) {
    throw new Error('useGalleryPageState must be used within a GalleryPageProvider');
  }
  return context;
};

type GalleryPageProviderProps = {
  children: React.ReactNode;
  galleryType: 'GalleryImages' | 'Video';
  useCaseTaxonomies?: any[] | null;
  productTaxonomies?: any[] | null;
  hideFilterBar?: boolean | null;
  hideSearchBar?: boolean | null;
};

export const GalleryPageProvider: React.FC<GalleryPageProviderProps> = ({
  children,
  galleryType,
  useCaseTaxonomies,
  productTaxonomies,
  hideFilterBar,
  hideSearchBar,
}) => {
  // Taxonomy data
  const propsProductTaxonomyIds = React.useMemo(
    () => productTaxonomies?.map(taxonomy => taxonomy.id),
    [productTaxonomies],
  );
  const propsUseCaseTaxonomyIds = React.useMemo(
    () => useCaseTaxonomies?.map(taxonomy => taxonomy.id),
    [useCaseTaxonomies],
  );

  const [useCaseTaxonomy, setUseCaseTaxonomy] = useState<TaxonomyItem[]>([]);
  const [productsTaxonomy, setProductsTaxonomy] = useState<TaxonomyItem[]>([]);

  // Filter states
  const [selectedUseCase, setSelectedUseCase] = useState<string>('');
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Gallery content
  const [galleryContent, setGalleryContent] = useState<(GalleryImageItem | VideoItem)[]>([]);
  const galleryContentValue = useMemo(() => galleryContent, [galleryContent]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // get current locale
  const locale = useLocale();

  // Function to fetch filtered content
  const fetchFilteredContent = async () => {
    try {
      setContentLoading(true);
      setError(null);

      // Build filter arrays
      const productIds = selectedProduct
        ? [selectedProduct, ...propsProductTaxonomyIds]
        : propsProductTaxonomyIds;
      const useCaseIds = selectedUseCase
        ? [selectedUseCase, ...propsUseCaseTaxonomyIds]
        : propsUseCaseTaxonomyIds;

      // Determine which collection to fetch based on galleryType
      // Note: block uses 'Video' but GraphQL schema uses 'Videos'
      const collectionName = galleryType === 'Video' ? 'Videos' : 'GalleryImages';

      let response;
      if (collectionName === 'GalleryImages') {
        response = await fetchGalleryImages(
          {
            productTaxonomyIds: productIds,
            useCaseTaxonomyIds: useCaseIds,
            searchText: searchQuery,
            limit: 50,
          },
          false,
          locale
        );
      } else {
        response = await fetchVideos(
          {
            productTaxonomyIds: productIds,
            useCaseTaxonomyIds: useCaseIds,
            searchText: searchQuery,
            limit: 50,
          },
          false,
          locale
        );
      }

      setGalleryContent(response.docs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
      console.error('Error fetching filtered content:', err);
    } finally {
      setContentLoading(false);
    }
  };

  // Effect to fetch taxonomies on mount
  useEffect(() => {
    const fetchTaxonomies = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch use case taxonomy
        const useCaseResponse = await fetch('/api/use-case-taxonomy?limit=100');
        if (!useCaseResponse.ok) {
          throw new Error(`Failed to fetch use case taxonomy: ${useCaseResponse.status}`);
        }
        const useCaseData: TaxonomyResponse = await useCaseResponse.json();

        // Fetch products taxonomy
        const productsResponse = await fetch('/api/products-taxonomy?limit=100');
        if (!productsResponse.ok) {
          throw new Error(`Failed to fetch products taxonomy: ${productsResponse.status}`);
        }
        const productsData: TaxonomyResponse = await productsResponse.json();

        setUseCaseTaxonomy(useCaseData.docs);
        setProductsTaxonomy(productsData.docs);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch taxonomies');
        console.error('Error fetching taxonomies:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTaxonomies();
  }, []);

  // Effect to fetch filtered content when filters change
  useEffect(() => {
    if (!loading) {
      // Only fetch content after taxonomies are loaded
      fetchFilteredContent();
    }
  }, [selectedUseCase, selectedProduct, searchQuery, loading, galleryType]);

  const contextValue: GalleryPageContextValue = {
    data: {
      useCaseTaxonomy,
      productsTaxonomy,
      selectedUseCase,
      selectedProduct,
      searchQuery,
      galleryContent: galleryContentValue,
      loading,
      contentLoading,
      error,
      useCaseTaxonomies,
      productTaxonomies,
      hideFilterBar,
      hideSearchBar,
    },
    actions: {
      setSelectedUseCase,
      setSelectedProduct,
      setSearchQuery,
      refreshContent: fetchFilteredContent,
    },
  };

  return <GalleryPageContext.Provider value={contextValue}>{children}</GalleryPageContext.Provider>;
};

export default GalleryPageProvider;
